// Nine Lives '84 - player settings.
//
// A few switches remembered between visits. The storage backend is
// injectable so the logic can be tested without a browser.

import type { KeyValueStore } from './hud/highscores';

export interface Settings {
  /** The classic four-colour view. */
  retro: boolean;
  /** No scene-transition dollies, no camera shake. */
  reduceMotion: boolean;
  /** No shadows or bloom, 1:1 pixels: for phones and integrated graphics. */
  lowDetail: boolean;
  /** 0 .. 1. */
  volume: number;
  /** The controls legend in the corner during play. */
  legend: boolean;
  /** Tunes on the title and during play (effects stay). */
  music: boolean;
}

const KEY = 'ninelives84.settings';
/** Where settings lived before the game was renamed; read if the new key is empty. */
const LEGACY_KEY = 'alleycat.settings';

export const DEFAULT_SETTINGS: Settings = {
  retro: false,
  reduceMotion: false,
  lowDetail: false,
  volume: 0.7,
  legend: true,
  music: true,
};

export function loadSettings(store: KeyValueStore | null, defaults = DEFAULT_SETTINGS): Settings {
  const s = { ...defaults };
  try {
    const raw = store?.getItem(KEY) ?? store?.getItem(LEGACY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Record<keyof Settings, unknown>>;
      if (typeof parsed.retro === 'boolean') s.retro = parsed.retro;
      if (typeof parsed.reduceMotion === 'boolean') s.reduceMotion = parsed.reduceMotion;
      if (typeof parsed.lowDetail === 'boolean') s.lowDetail = parsed.lowDetail;
      if (typeof parsed.legend === 'boolean') s.legend = parsed.legend;
      if (typeof parsed.music === 'boolean') s.music = parsed.music;
      if (typeof parsed.volume === 'number' && Number.isFinite(parsed.volume)) {
        s.volume = Math.max(0, Math.min(1, parsed.volume));
      }
    }
  } catch {
    // Unreadable storage: keep the defaults.
  }
  return s;
}

export function saveSettings(store: KeyValueStore | null, s: Settings): void {
  try {
    store?.setItem(KEY, JSON.stringify(s));
  } catch {
    // Storage may be unavailable (private mode); the in-memory copy still applies.
  }
}

/** A sensible starting point for a device: phones and tablets get low detail. */
export function defaultsForDevice(touch: boolean, prefersReducedMotion: boolean): Settings {
  return {
    ...DEFAULT_SETTINGS,
    lowDetail: touch,
    reduceMotion: prefersReducedMotion,
  };
}
